import _ from 'lodash'
import type {
	LangsToPagesData,
	PageData,
	PageDefinition,
	PageFullPath,
	PageIdToAlLLangUriSeo,
	PageIdToPath,
	PageUriSeo,
	PageUriSEOPrimaryToCurrent,
	SeoTranslationData,
} from '@wix/thunderbolt-becky-types'

export const addUrlProtocols = (url: string): string => {
	const protocols = ['https://', 'http://', '//']
	const hasProtocol = _.some(protocols, _.includes.bind(null, url))
	return hasProtocol ? url : _.last(protocols) + url
}

const getPageDataParentId = (parentId: PageData | string | undefined) =>
	(parentId as PageData)?.id || (parentId as string)

export const mapPageIdToRoute = (
	pageDataItems: Record<string, Pick<PageDefinition['data'], 'id' | 'pageUriSEO' | 'parentPageId'>>
) =>
	Object.values(pageDataItems).reduce((pageIdToRouteAcc: { [pageId: string]: string }, pageData) => {
		if (!pageIdToRouteAcc[pageData.id]) {
			let currPageData = pageData
			const pageIdsToResolveQueue: Array<string> = []

			while (currPageData) {
				pageIdsToResolveQueue.push(currPageData.id)
				currPageData = pageDataItems[getPageDataParentId(currPageData.parentPageId)]
			}

			while (pageIdsToResolveQueue.length) {
				const pageId = pageIdsToResolveQueue.pop() as string
				const { parentPageId, pageUriSEO } = pageDataItems[pageId]
				pageIdToRouteAcc[pageId] = [pageIdToRouteAcc[getPageDataParentId(parentPageId)], pageUriSEO]
					.filter((x) => x)
					.join('/')
			}
		}

		return pageIdToRouteAcc
	}, {})

const getPageIdToAllPageUriSEO = (seoTranslatedData: SeoTranslationData): PageIdToAlLLangUriSeo => {
	return _.reduce(
		seoTranslatedData.default,
		(acc: PageIdToAlLLangUriSeo, pageSeoData, pageId) => {
			acc[pageId] = {
				primary: {
					pageUriSeo: pageSeoData.pageUriSEO,
					...(pageSeoData.parentPageId && { parentPageId: pageSeoData.parentPageId }),
				},
			}
			_.forEach(seoTranslatedData, (langValue, langKey) => {
				if (langKey !== 'default' && langValue?.[pageId]) {
					acc[pageId][langKey] = {
						pageUriSeo: langValue[pageId].pageUriSEO,
						...(pageSeoData.parentPageId && { parentPageId: pageSeoData.parentPageId }),
					}
				}
			})
			return acc
		},
		{}
	)
}

const getPathPerLang = (
	seoTranslatedData: SeoTranslationData,
	pagesIdsToAllLang: PageIdToAlLLangUriSeo,
	disableStaticPagesUrlHierarchy: boolean | undefined
): LangsToPagesData => {
	const pathPerLanguage = _.mapValues(seoTranslatedData, (data, languageData) => {
		return _.reduce(
			pagesIdsToAllLang,
			(acc: PageIdToPath, pageData, pageId) => {
				let current = pageData
				let pageRoute = current[languageData] || current.primary
				const pageUriSeo = pageRoute.pageUriSeo
				let fullPath = `${pageRoute.pageUriSeo}`
				let currentPageId = pageRoute.parentPageId
				while (currentPageId && pagesIdsToAllLang[currentPageId]) {
					current = pagesIdsToAllLang[currentPageId]
					pageRoute = current[languageData] || current.primary
					fullPath = `${pageRoute.pageUriSeo}/${fullPath}`
					currentPageId = pageRoute.parentPageId
				}
				acc[pageId] = disableStaticPagesUrlHierarchy ? pageUriSeo : fullPath
				return acc
			},
			{}
		)
	})

	return { primary: pathPerLanguage.default, ..._.omit(pathPerLanguage, 'default') }
}

const getPathPrimaryToTranslation = (
	primaryData: { [pageId: string]: PageUriSeo | PageFullPath },
	currentLangData: { [pageId: string]: PageUriSeo | PageFullPath }
): PageUriSEOPrimaryToCurrent => {
	return _.reduce(
		primaryData,
		(acc: PageUriSEOPrimaryToCurrent, pageData, pageId) => {
			acc[pageData] = currentLangData[pageId]
			return acc
		},
		{}
	)
}

export const getPagesUriSeoMLConfig = (
	seoTranslations: SeoTranslationData,
	currentLanguage: string,
	disableStaticPagesUrlHierarchy: boolean | undefined
) => {
	const pagesIdsToAllLang = getPageIdToAllPageUriSEO(seoTranslations)

	const languagesToPagesData = getPathPerLang(seoTranslations, pagesIdsToAllLang, disableStaticPagesUrlHierarchy)
	const currentLangData = languagesToPagesData[currentLanguage] || languagesToPagesData.primary
	const primaryToCurrentLang = getPathPrimaryToTranslation(languagesToPagesData.primary, currentLangData)

	return { primaryToCurrentLang, languagesToPagesData }
}
